/**
 * Leer un QR de una imagen EN EL NAVEGADOR (jsqr sobre un canvas). La imagen
 * nunca sale de la máquina de la persona: ni al backend ni a un modelo. Lo usa
 * el asistente de MFA del Vault para la captura de "Exportar cuentas" de
 * Google Authenticator o del QR de configuración de una plataforma.
 *
 * Prueba la imagen a varias escalas (capturas de 3000 px o miniaturas chicas)
 * y con inversión de colores (modo oscuro).
 */
import jsQR from 'jsqr';

const MAX_SIDE = 2400;
const SCALES = [1, 0.5, 2, 0.25];

async function toBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      /* Safari viejo / formato raro: cae al <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function decodeQrFromImage(file: Blob): Promise<string | null> {
  const src = await toBitmap(file);
  const w0 = 'naturalWidth' in src ? src.naturalWidth : src.width;
  const h0 = 'naturalHeight' in src ? src.naturalHeight : src.height;
  if (!w0 || !h0) return null;
  const base = Math.min(1, MAX_SIDE / Math.max(w0, h0));
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  try {
    for (const s of SCALES) {
      const w = Math.max(1, Math.round(w0 * base * s));
      const h = Math.max(1, Math.round(h0 * base * s));
      if (w > MAX_SIDE * 1.5 || h > MAX_SIDE * 1.5 || w < 80 || h < 80) continue;
      canvas.width = w;
      canvas.height = h;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(src, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h);
      const hit = jsQR(data.data, w, h, { inversionAttempts: 'attemptBoth' });
      if (hit?.data) return hit.data;
    }
    return null;
  } finally {
    if ('close' in src) src.close();
  }
}

/** La imagen que viene en un evento de pegar o arrastrar, si hay. */
export function imageFromDataTransfer(dt: DataTransfer | null | undefined): File | null {
  if (!dt) return null;
  for (const f of Array.from(dt.files ?? [])) if (f.type.startsWith('image/')) return f;
  for (const it of Array.from(dt.items ?? [])) {
    if (it.kind === 'file' && it.type.startsWith('image/')) {
      const f = it.getAsFile();
      if (f) return f;
    }
  }
  return null;
}
